import { observable } from '@legendapp/state';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';

/**
 * Today's reading activity store - tracks blocks read today
 * Uses local-first approach with periodic server sync
 */
export const todayActivity$ = observable({
  // Local optimistic counter
  todayBlocks: 0,
  // Last synced value from server (for drift detection)
  serverValue: 0,
  // Loading state
  loading: true,
  // Last sync timestamp
  lastSync: null,
  // Whether initialized
  initialized: false,
});

/**
 * Get start of today in local timezone as ISO string
 */
const getTodayStart = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.toISOString();
};

/**
 * Fetch today's blocks from server
 */
export const fetchTodayBlocks = async () => {
  const userId = auth$.user.get()?.id;
  if (!userId) {
    todayActivity$.loading.set(false);
    return 0;
  }

  try {
    const { data, error } = await supabase
      .from('reedle_reading_activity')
      .select('block_count')
      .eq('user_id', userId)
      .gte('hour_bucket', getTodayStart());

    if (error) throw error;

    const total = (data || []).reduce((sum, row) => sum + (row.block_count || 0), 0);
    
    todayActivity$.assign({
      todayBlocks: total,
      serverValue: total,
      loading: false,
      lastSync: Date.now(),
      initialized: true,
    });
    
    return total;
  } catch (err) {
    console.error('Failed to fetch today blocks:', err);
    todayActivity$.loading.set(false);
    return 0;
  }
};

/**
 * Increment local counter (called when block is marked as read)
 */
export const incrementTodayBlocks = () => {
  todayActivity$.todayBlocks.set(v => v + 1);
};

/**
 * Decrement local counter (called when block is marked as unread)
 * Only for current hour's blocks - minimum 0
 */
export const decrementTodayBlocks = () => {
  todayActivity$.todayBlocks.set(v => Math.max(0, v - 1));
};

/**
 * Sync with server - verify local count matches server
 * Corrects any drift silently
 */
export const syncTodayBlocks = async () => {
  const serverTotal = await fetchTodayBlocks();
  return serverTotal;
};

/**
 * Initialize the store - call on app mount
 */
export const initTodayActivity = async () => {
  if (todayActivity$.initialized.get()) return;
  todayActivity$.initialized.set(true); // synchronous guard — prevents race condition from concurrent calls
  await fetchTodayBlocks();
  
  // Set up periodic sync every 60 seconds
  setInterval(syncTodayBlocks, 60000);
  
  // Sync on visibility change (tab regains focus)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      syncTodayBlocks();
    }
  });
};

/**
 * Reset for new day (call at midnight or on date change detection)
 */
export const resetTodayActivity = () => {
  todayActivity$.assign({
    todayBlocks: 0,
    serverValue: 0,
    lastSync: null,
  });
};

/**
 * Get current today blocks count
 */
export const getTodayBlocks = () => todayActivity$.todayBlocks.get();

